// src/layouts/AuthenticatedLayout.js
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import Sidebar, { SidebarToggleButton } from '../components/Sidebar';
import Header from '../components/Header';
import ErrorBoundary, { RouteErrorBoundary } from '../components/ErrorBoundary';
import { selectUser } from '../store/slices/authSlice';
import { useThemeContext } from '../components/ThemeProvider';


const AuthenticatedLayout = () => {
  const user = useSelector(selectUser);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Dinamik tema renklerini al
  const { primaryColor } = useThemeContext();    

  // Handle mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Auto collapse on mobile
      if (mobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [sidebarCollapsed]);

  // Toggle sidebar handler
  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Global event ile sidebar'ı kapat
  useEffect(() => {
    const closeHandler = () => setSidebarCollapsed(true);
    window.addEventListener('app:closeSidebar', closeHandler);
    return () => window.removeEventListener('app:closeSidebar', closeHandler);
  }, []);

  // Enhanced Header component that includes the sidebar toggle
  const EnhancedHeader = () => (
    <div className="relative">
      <Header />
      {/* Desktop Sidebar Toggle Button positioned correctly */}
      {!isMobile && (
        <motion.div
          className="absolute left-6 top-1/2 -translate-y-1/2 z-40"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <SidebarToggleButton
            isCollapsed={sidebarCollapsed}
            onToggle={handleToggleSidebar}
            primaryColor={primaryColor}
          />
        </motion.div>
      )}
    </div>
  );

  if (!user) {
    return null;
  }

  return (
    <ErrorBoundary
      title="Layout Hatası"
      message="Ana sayfa düzeni yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin."
      showDetails={process.env.NODE_ENV === 'development'}
      onError={(error, errorInfo) => {
        console.error('🚨 AuthenticatedLayout Error:', { error, errorInfo });
      }}
    >
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
        {/* Sidebar */}
        <ErrorBoundary
          title="Menü Hatası"
          message="Yan menü yüklenirken bir hata oluştu."
          showDetails={false}
          showGoHome={false}
          showLogout={true}
        >
          <motion.div
            className="relative flex-shrink-0"
            animate={{
              width: isMobile ? 0 : (sidebarCollapsed ? 72 : 280)
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Sidebar 
              isCollapsed={sidebarCollapsed}
              onToggle={handleToggleSidebar}
              isMobile={isMobile}
            />
          </motion.div>
        </ErrorBoundary>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <ErrorBoundary
            title="Başlık Hatası"
            message="Sayfa başlığı yüklenirken bir hata oluştu."
            showDetails={false}
            showGoHome={false}
            showLogout={true}
          >
            <EnhancedHeader />
          </ErrorBoundary>

          {/* Page Content */}
          <motion.main 
            className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div className="container mx-auto px-4 py-6 lg:px-6 lg:py-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={window.location.pathname}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="h-full"
                >
                  {/* Route-specific Error Boundary */}
                  <RouteErrorBoundary>
                    <Outlet />
                  </RouteErrorBoundary>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AuthenticatedLayout;